import {
  AppCard,
  Button,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiContentSaveOutline } from "@mdi/js";
import { Box, CircularProgress } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  getShowOtherMasterdataServicesRequestQueryKey,
  useShowOtherMasterdataServicesRequest,
  useUpdateOtherMasterdataServicesRequest,
} from "../../../../../__generated__/orval/endpoints/other-masterdata-services-requests/other-masterdata-services-requests";
import { ConfirmationModal } from "../../../../../components/molecules/ConfirmationModal";
import { OtherMasterdataForm } from "../-internal-components/OtherMasterdataForm";
import {
  type OtherMasterdataSchema,
  otherMasterdataSchema,
} from "../-schemas/otherMasterdataSchema";

const toPreviousTicketId = (value?: string): number | undefined => {
  if (!value) {
    return undefined;
  }

  const id = Number(value);

  return Number.isFinite(id) ? id : undefined;
};

const toMatnumbers = (
  values?: Array<string | { matnumber: string }>,
): string[] | undefined => {
  if (!values?.length) {
    return undefined;
  }

  const matnumbers = values
    .map((value) => (typeof value === "string" ? value : value.matnumber))
    .map((value) => value.trim())
    .filter((value) => value.length > 0);

  return matnumbers.length ? matnumbers : undefined;
};

const EditOtherMasterdataServicesRequest: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { otherMasterdataServicesRequestId } = Route.useParams();
  const id = Number(otherMasterdataServicesRequestId);
  const queryClient = useQueryClient();

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [formData, setFormData] = useState<OtherMasterdataSchema | null>(null);

  const { control, handleSubmit, reset, setValue } = useForm({
    defaultValues: {
      backup_requester: null,
      description: "",
      file_attachments: [],
      is_repetro: null,
      matnumber: [],
      previous_ticket: "",
      quantity_of_materials: "",
      requester_field: null,
      subject: "",
    },
    resolver: zodResolver(otherMasterdataSchema),
  });

  const { data, isLoading, isError } =
    useShowOtherMasterdataServicesRequest(id);
  const mmRequestId = data?.mm_request?.id;
  const title = mmRequestId
    ? `${t("common.otherMasterdataServicesRequest")} #${mmRequestId}`
    : t("common.otherMasterdataServicesRequest");

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(t("mmRequests.fetchError"), { variant: "error" });
    }
  }, [isError, t]);

  useEffect(() => {
    if (!data) return;

    const mmRequest = data.mm_request as typeof data.mm_request & {
      previous_ticket_id?: number | null;
    };

    const matnumber = Array.isArray(mmRequest.matnumber)
      ? mmRequest.matnumber.filter((value) => value.length > 0)
      : [];

    reset({
      backup_requester: mmRequest.backup_requester
        ? {
            full_name_with_login:
              mmRequest.backup_requester.full_name_with_login,
            id: mmRequest.backup_requester.id,
          }
        : null,
      description: mmRequest.description ?? "",
      file_attachments: [],
      is_repetro: (data.is_repetro ||
        null) as OtherMasterdataSchema["is_repetro"],
      matnumber,
      previous_ticket: mmRequest.previous_ticket_id
        ? String(mmRequest.previous_ticket_id)
        : "",
      quantity_of_materials: mmRequest.quantity_of_materials ?? "",
      requester_field: mmRequest.requester_field
        ? { id: mmRequest.requester_field, name: mmRequest.requester_field }
        : null,
      subject: mmRequest.subject ?? "",
    });
  }, [data, reset]);

  const { mutate, isPending } = useUpdateOtherMasterdataServicesRequest({
    mutation: {
      onError: () => {
        enqueueSnackbar(t("mmRequestChild.updateErrorMessage"), {
          variant: "error",
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getShowOtherMasterdataServicesRequestQueryKey(id),
        });
        enqueueSnackbar(t("mmRequestChild.updateSuccessMessage"), {
          variant: "success",
        });
        navigate({
          params: { otherMasterdataServicesRequestId },
          to: "/vbuyer/other_masterdata_services_requests/$otherMasterdataServicesRequestId",
        });
      },
    },
  });

  const onSubmit: SubmitHandler<OtherMasterdataSchema> = useCallback(
    (values) => {
      setFormData(values);
      setOpenConfirmModal(true);
    },
    [],
  );

  const handleConfirm = useCallback(() => {
    if (!formData) return;

    setOpenConfirmModal(false);

    mutate({
      data: {
        other_masterdata_services_request: {
          is_repetro: formData.is_repetro || undefined,
          mm_request_attributes: {
            description: formData.description,
            matnumber: toMatnumbers(formData.matnumber),
            previous_ticket_id: toPreviousTicketId(formData.previous_ticket),
            quantity_of_materials: formData.quantity_of_materials || undefined,
            requester_field: formData.requester_field?.name ?? "",
            subject: formData.subject,
          },
        },
      },
      id,
    });
  }, [formData, id, mutate]);

  const handleFilesChange = useCallback(() => {}, []);

  if (isLoading) {
    return (
      <PageContainer
        backTo={`/vbuyer/other_masterdata_services_requests/${otherMasterdataServicesRequestId}`}
        maxWidth="md"
        showBackButton
        title={title}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      backTo={`/vbuyer/other_masterdata_services_requests/${otherMasterdataServicesRequestId}`}
      maxWidth="md"
      showBackButton
      title={title}
    >
      <AppCard>
        <OtherMasterdataForm
          control={control}
          onFilesChange={handleFilesChange}
          onSubmit={handleSubmit(onSubmit)}
          setValue={setValue}
          showBackupRequester={false}
          showFileUpload={false}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              color="success"
              loading={isPending}
              startIcon={<Icon icon={mdiContentSaveOutline} />}
              type="submit"
            >
              {t("common.save")}
            </Button>
          </Box>
        </OtherMasterdataForm>
      </AppCard>

      <ConfirmationModal
        handleOk={handleConfirm}
        message={t("mmRequestChild.updateModalMessage")}
        open={openConfirmModal}
        setOpen={setOpenConfirmModal}
        title={t("mmRequestChild.updateModalTitle")}
      />
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/other_masterdata_services_requests/$otherMasterdataServicesRequestId/edit",
)({
  component: EditOtherMasterdataServicesRequest,
});
